import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import store from 'src/js/store';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import {
  i18next,
  site,
  siteLocales,
  dialogs,
  saving,
  template,
  hooks,
  integration,
  readOnly,
  errors,
  view,
  blocks,
} from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import withFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hoc/with-feature-flags';
import segment from 'src/js/modules/analytics/segment';
import websiteSettings from 'src/js/modules/website-settings';
import saveSite, { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import { verifiers, selectors } from '@yola/subscription-manager-js';
import getFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-feature-limits';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import PageManager from '../components/page-manager';
import getOptionsToCreatePage from '../helpers/get-options-to-create-page';
import generatePageName from '../helpers/generate-page-title';
import getNewPageModalCaptions from '../helpers/get-new-page-modal-captions';
import getUpgradeBannerVisibilityItems from '../helpers/get-upgrade-banner-visibility-items';
import buildPageTreeItems from '../helpers/build-page-tree-items';
import buildSelectedItemsMap from '../helpers/build-selected-items-map';
import toggleItemsSelection from '../helpers/toggle-items-selection';
import getMoveToFolderTooltip from '../helpers/get-move-to-folder-tooltip';
import { getFolderSelectionStateById } from '../helpers/get-folder-selection-state';
import performFolderDelete from '../helpers/perform-folder-delete';
import constants, { ROOT_ID } from '../constants';
import isPageCreationAvailable from '../verifiers/is-page-creation-available';
import injectStoreId from '../../onlinestore/helpers/inject-store-id';
import fieldNames from '../constants/field-names';
import pageTypes from '../constants/page-types';
import itemTypes from '../constants/item-types';
import insertAllBlocksToThePage from '../helpers/insert-all-blocks-to-the-page';
import ai from '../../ai';
import settingTypes from '../../website-settings/constants/setting-types';
import reloadSiteMeta from '../../ai-wizard/helpers/reload-site-meta';
import triggerIds from '../../analytics/segment/constants/trigger-ids';

const { Paragraph } = designSystem;

const {
  trackers: {
    trackEvent,
    trackPageManagerSearchPerformed,
    trackPageManagerSearchClicked,
    trackAddAiPageTriggerClicked,
  },
  constants: { events },
} = segment;

const { helpers } = websiteSettings;

const {
  constants: { errorTypes, sourceIds },
  helpers: { initiatePageReplacement },
} = ai;

function createFilterCb(pages, locale) {
  const pageManagerOptions = [pageTypes.ABOUT_US, pageTypes.CONTACT_US];
  const targetPages = pages.filter((page) => page.locale === locale);

  return (option) =>
    pageManagerOptions.includes(option.id) &&
    !targetPages.some((page) => page.recipeId === option.id);
}

const getCaptions = () => ({
  searchPlaceholder: `${i18next.t('Search')}...`,
  addPage: i18next.t('Add page'),
  cancel: i18next.t('Cancel'),
  select: i18next.t('Select'),
  batchDelete: i18next.t('Delete'),
  setAsHomePage: i18next.t('Use as Homepage'),
  fullscreenTooltip: i18next.t('Full screen'),
  title: i18next.t('Page manager'),
  searchEmptyTitle: i18next.t('Sorry!'),
  searchEmptyText: i18next.t('Nothing was found'),
  pageTemplatesSection: i18next.t('Generate pages with AI'),
  hoveredTemplateControl: i18next.t('Generate'),
  createFolder: i18next.t('Create a folder'),
  addNewPage: i18next.t('Add a new page'),
  pages: i18next.t('Your pages'),
  selected: i18next.t('selected'),
  noItemsSelectedTooltip: i18next.t('Select pages or folders to continue'),
});

// eslint-disable-next-line yola/react/prop-types
const DeleteTitleComponent = ({ children }) => (
  <Paragraph appearance="danger">{children}</Paragraph>
);

function PageManagerContainer(props) {
  const {
    pages,
    folders,
    locale,
    onClose,
    activePageId,
    pageTemplates,
    featureFlags,
    currentPackage,
    upsells,
    currentPackageType,
    onSetActivePage,
    savingStatus,
    onUnsavedActivePageChange,
    html,
    htmlCheckpoint,
    onCopyPage,
    onAddNewPageClick,
    onAddNewPage,
    onPageSettingsClick,
    onDeletePageClick,
    onSetHomePageClick,
    onDeveloperConsoleClick,
    onGeneratePageCopy,
    openPageCreation,
    onCreateFolderClick,
    onFolderSettingsClick,
    onDeleteFolderClick,
    onItemMove,
    isReadOnlyModeEnabled,
    siteSettings,
    businessCategory,
    getSharedData,
    resolveSharedData,
  } = props;
  const captions = getCaptions();
  const {
    developer_console: isDeveloperConsoleEnabled,
    ai_page_wizard: aiPageWizardEnabled,
    ai_generate_page_copy: aiGeneratePageCopyEnabled,
    page_folders: isPageFoldersEnabled,
  } = featureFlags;

  const [filtered, setFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemsMap, setSelectedItemsMap] = useState({});
  const [selectModeEnabled, setSelectModeEnabled] = useState(false);
  const sharedData = useMemo(() => getSharedData(), [getSharedData]);
  const pageManagerTreeState = useMemo(() => sharedData?.pageManagerTreeState, [sharedData]);
  const savedOpenFolderIds = pageManagerTreeState?.openNodeIds;
  const scrollToItemId = pageManagerTreeState?.scrollToItemId ?? null;

  const optionsToCreatePage = getOptionsToCreatePage(createFilterCb(pages, locale));

  function onNestedDialogSubmit(callback, ...data) {
    const { UNSAVED } = saving.constants.statuses;

    if (savingStatus === UNSAVED && html !== htmlCheckpoint) {
      onUnsavedActivePageChange(() => callback(...data));
    } else {
      callback(...data);
    }
  }

  function onItemClick(item) {
    if (item.isFolder) {
      return;
    }

    const { UNSAVED } = saving.constants.statuses;

    // Do nothing if user clicks on active page
    if (item.id === activePageId) {
      return;
    }

    if (savingStatus === UNSAVED && html !== htmlCheckpoint) {
      onUnsavedActivePageChange(onSetActivePage.bind(null, item.id));
    } else {
      onSetActivePage(item.id);
    }
  }

  function handleCopyPage(e, targetPageId) {
    e.stopPropagation();
    const isCopiedPage = true;
    onAddNewPageClick({
      pageTitle: generatePageName(pages, targetPageId),
      captions: getNewPageModalCaptions(isCopiedPage),
      isDisplayInNavAvailable: template.verifiers.isVariableGlobalsAvailable('navigation'),
      onSubmit: (values) => onNestedDialogSubmit(onCopyPage, targetPageId, locale, values),
    });
  }

  function handleTranslatePages({ pageIds, bulkEditing = false } = {}) {
    if (isReadOnlyModeEnabled) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }

    dialogs.operations.show(dialogTypes.TRANSLATE_PAGE, {
      initialValues: {
        pageIds,
      },
      bulkEditing,
    });
  }

  function handleMoveItems({ pageIds, folderIds, bulkEditing } = {}) {
    if (isReadOnlyModeEnabled) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }

    const itemsCount = pageIds.length + folderIds.length;

    dialogs.operations.show(dialogTypes.BULK_EDITING_MOVE_TO_DIALOG, {
      pageIds,
      folderIds,
      bulkEditing,
      itemsCount,
    });
  }

  function handleDeleteItems({ pageIds, folderIds, bulkEditing } = {}) {
    if (isReadOnlyModeEnabled) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }

    const itemsCount = pageIds.length + folderIds.length;

    const getItemType = () => {
      if (!pageIds.length) {
        return itemTypes.FOLDER;
      }

      if (!folderIds.length) {
        return itemTypes.PAGE;
      }

      return 'all';
    };

    dialogs.operations.show(dialogTypes.BULK_EDITING_DELETE_DIALOG, {
      pageIds,
      folderIds,
      itemType: getItemType(),
      bulkEditing,
      itemsCount,
    });
  }

  function handleAddNewPage() {
    const { ai_page_wizard: isAiPageWizardEnabled } = featureFlags;
    const isGenerateWithAiAvailable = isAiPageWizardEnabled;
    const isCopiedPage = false;

    onAddNewPageClick({
      pageTitle: generatePageName(pages),
      pageTemplate: constants.BLANK_PAGE_TEMPLATE_ID,
      captions: getNewPageModalCaptions(isCopiedPage, isGenerateWithAiAvailable),
      isDisplayInNavAvailable: template.verifiers.isVariableGlobalsAvailable('navigation'),
      isGenerateWithAiAvailable,
      onSubmit: (values) => onNestedDialogSubmit(onAddNewPage, values),
    });
  }

  function handleGeneratePageCopy(pageId) {
    const shouldShowAiOnboarding =
      !siteSettings[settingTypes.BUSINESS_NAME] || !siteSettings[settingTypes.BUSINESS_DESCRIPTION];

    trackEvent(events.AI_GENERATE_PAGE_COPY_CLICKED);

    const generatePageCopy = () => onGeneratePageCopy(pageId, locale);
    const showLoadingDialog = () =>
      dialogs.operations.show(dialogTypes.UPLOAD_PROGRESS_DIALOG, {
        overlay: 'visible',
        cancelable: false,
        progressType: 'indeterminate',
        width: 336,
        height: 109,
        minHeight: 109,
        captions: {
          title: `${i18next.t('Generating page copy')}...`,
        },
      });

    saveSiteWithEffect(() => {
      if (shouldShowAiOnboarding) {
        dialogs.operations.show(dialogTypes.AI_ONBOARDING_DIALOG, {
          sourceId: sourceIds.GENERATE_PAGE_COPY,
          title: i18next.t('Generate page copy'),
          onBeforeSave: showLoadingDialog,
          onDialogMainAction: generatePageCopy,
        });

        return;
      }

      showLoadingDialog();
      generatePageCopy();
    });
  }

  function handleSetHomePage(e, item) {
    e.stopPropagation();
    onSetHomePageClick(item.id, locale);
  }

  function handleOpenPageCreation(pageTypeItem) {
    const { id = pageTypes.EMPTY_PAGE } = pageTypeItem || {};

    if (id !== pageTypes.EMPTY_PAGE) {
      trackAddAiPageTriggerClicked({
        recipeId: id,
        businessCategory,
      });
    }

    const triggerId =
      id === pageTypes.EMPTY_PAGE ? triggerIds.ADD_PAGE_PLUS_BUTTON : triggerIds.AI_PAGE;

    openPageCreation({
      pageType: id,
      onAddPageFromTemplate: onAddNewPage,
      triggerId,
    });
  }

  function handleEditPageSetting(e, item) {
    e.stopPropagation();
    onPageSettingsClick(item.id);
  }

  function handleDeletePage(e, item) {
    e.stopPropagation();
    onDeletePageClick(item.id, item.name, selectModeEnabled);
  }

  function handleOpenDeveloperConsole(event, item) {
    event.stopPropagation();
    onDeveloperConsoleClick(item);
  }

  function handleAddNewPageClick() {
    if (aiPageWizardEnabled) {
      handleOpenPageCreation();
    } else {
      handleAddNewPage();
    }

    trackEvent(events.ADD_PAGE_TRIGGER_CLICKED, { triggerId: triggerIds.ADD_PAGE_PLUS_BUTTON });
  }

  function handleCreateFolderClick() {
    trackEvent(events.ADD_FOLDER_TRIGGER_CLICKED, {
      triggerId: triggerIds.ADD_FOLDER_PLUS_BUTTON,
    });
    onCreateFolderClick(locale);
  }

  function handleFolderSettingsClick(e, folder) {
    e.stopPropagation();
    onFolderSettingsClick(folder.id, folder.name);
  }

  function handleDeleteFolderClick(e, folder) {
    e.stopPropagation();
    onDeleteFolderClick(folder.id, folder.name, selectModeEnabled, folder.isFolderEmpty);
  }

  function handleMoveToFolderClick(e, { pageIds, folderIds }) {
    e.stopPropagation();
    handleMoveItems({ pageIds, folderIds, bulkEditing: false });
  }

  function handleAddPageToFolder(e, folderId) {
    e.stopPropagation();
    if (aiPageWizardEnabled) {
      openPageCreation({
        pageType: pageTypes.EMPTY_PAGE,
        onAddPageFromTemplate: onAddNewPage,
        triggerId: triggerIds.ADD_PAGE_PLUS_BUTTON,
        parentFolderId: folderId,
      });
    } else {
      const isCopiedPage = false;
      onAddNewPageClick({
        pageTitle: generatePageName(pages),
        pageTemplate: constants.BLANK_PAGE_TEMPLATE_ID,
        captions: getNewPageModalCaptions(isCopiedPage),
        isDisplayInNavAvailable: template.verifiers.isVariableGlobalsAvailable('navigation'),
        onSubmit: (values) => onNestedDialogSubmit(onAddNewPage, values, folderId),
        parentFolderId: folderId,
      });
    }

    trackEvent(events.ADD_PAGE_TRIGGER_CLICKED, { triggerId: triggerIds.ADD_PAGE_PLUS_FOLDER });
  }

  function filter(value) {
    if (!value) {
      setFiltered(false);
      setSearchQuery('');
      return;
    }
    const inputText = value.trim().toLowerCase();
    const matches = pages.filter((page) => page.name.toLowerCase().includes(inputText));

    trackPageManagerSearchPerformed({
      searchTerm: value,
      searchResults: matches.length,
    });

    setFiltered(true);
    setSearchQuery(inputText);
  }

  const pagesFeatureLimit = getFeatureLimits(currentPackage).pagesCount;

  const isFreePackageType = verifiers.isFreeHostingPackageType(currentPackageType);

  const upgradeBannerVisibilityItems = getUpgradeBannerVisibilityItems({
    isFreePackageType,
    pagesFeatureLimit,
    pagesAmount: pages.length,
  });

  const allLocalePages = useMemo(
    () => pages.filter((page) => page.locale === locale),
    [pages, locale]
  );

  const pagesForLocale = useMemo(() => {
    const localePages = pages.filter((page) => page.locale === locale);
    if (!filtered || !searchQuery) return localePages;
    return localePages.filter((page) => page.name.toLowerCase().includes(searchQuery));
  }, [filtered, searchQuery, pages, locale]);

  const foldersForLocale = useMemo(
    () => folders.filter((folder) => folder.locale === locale),
    [folders, locale]
  );

  const filteredFolders = useMemo(() => {
    if (!isPageFoldersEnabled) return [];
    if (!filtered || !searchQuery) return foldersForLocale;

    const foldersById = new Map(foldersForLocale.map((folder) => [folder.id, folder]));
    const matchedFolderIds = new Set();

    const addWithAncestors = (folderId) => {
      let currentId = folderId;
      while (currentId && !matchedFolderIds.has(currentId)) {
        matchedFolderIds.add(currentId);
        currentId = foldersById.get(currentId)?.parentFolderId;
      }
    };

    foldersForLocale.forEach((folder) => {
      const matchesName = (folder.name || '').toLowerCase().includes(searchQuery);
      const hasMatchingChild = pagesForLocale.some((page) => page.parentFolderId === folder.id);
      if (matchesName || hasMatchingChild) {
        addWithAncestors(folder.id);
      }
    });

    return foldersForLocale.filter((folder) => matchedFolderIds.has(folder.id));
  }, [isPageFoldersEnabled, foldersForLocale, filtered, searchQuery, pagesForLocale]);

  const defaultOpenFolderIdsOnSearch = useMemo(() => {
    if (!filtered || !searchQuery) return null;

    return filteredFolders.map((folder) => folder.id);
  }, [filtered, searchQuery, filteredFolders]);

  const items = buildPageTreeItems(pagesForLocale, {
    folders: filteredFolders,
    allPages: allLocalePages,
    allFolders: foldersForLocale,
    isPageFoldersEnabled: Boolean(isPageFoldersEnabled),
    isHomePage: (item) => site.verifiers.isHomePage(item.path, helpers.getLanguageCodes()),
    activePageId,
    isDeveloperConsoleEnabled: Boolean(isDeveloperConsoleEnabled || isDeveloperMode()),
    aiGeneratePageCopyEnabled: Boolean(aiGeneratePageCopyEnabled),
    onSetHomePageClick: handleSetHomePage,
    onCopyPageClick: handleCopyPage,
    onPageSettingsClick: handleEditPageSetting,
    onTranslatePageClick: (e, itemId) => {
      e.stopPropagation();
      handleTranslatePages({ pageIds: [itemId] });
    },
    onGeneratePageCopyClick: handleGeneratePageCopy,
    onDeveloperConsoleClick: handleOpenDeveloperConsole,
    onDeletePageClick: handleDeletePage,
    onFolderSettingsClick: handleFolderSettingsClick,
    onDeleteFolderClick: handleDeleteFolderClick,
    onMoveToFolderClick: handleMoveToFolderClick,
    onAddPageToFolder: handleAddPageToFolder,
  });

  const localeFolderIds = useMemo(
    () => new Set(foldersForLocale.map((folder) => folder.id)),
    [foldersForLocale]
  );

  const parentIdByItemId = useMemo(() => {
    const map = {};

    foldersForLocale.forEach((folder) => {
      map[folder.id] = folder.parentFolderId || ROOT_ID;
    });
    allLocalePages.forEach((page) => {
      map[page.id] = page.parentFolderId || ROOT_ID;
    });

    return map;
  }, [foldersForLocale, allLocalePages]);

  const childIdsByParentId = useMemo(
    () =>
      Object.keys(parentIdByItemId).reduce((acc, itemId) => {
        const parentId = parentIdByItemId[itemId];
        if (!acc[parentId]) {
          acc[parentId] = [];
        }
        acc[parentId].push(itemId);
        return acc;
      }, {}),
    [parentIdByItemId]
  );

  const getNestedChildIds = useCallback(
    (parentId) => {
      const childIds = childIdsByParentId[parentId] || [];

      return childIds.reduce((acc, childId) => {
        acc.push(childId);
        acc.push(...getNestedChildIds(childId));
        return acc;
      }, []);
    },
    [childIdsByParentId]
  );

  const getAncestorFolderIds = useCallback(
    (itemId) => {
      const ancestorIds = [];
      let parentId = parentIdByItemId[itemId];

      while (parentId && parentId !== ROOT_ID && localeFolderIds.has(parentId)) {
        ancestorIds.push(parentId);
        parentId = parentIdByItemId[parentId];
      }

      return ancestorIds;
    },
    [parentIdByItemId, localeFolderIds]
  );

  const getTargetIds = useCallback(
    (itemId) => (localeFolderIds.has(itemId) ? [itemId, ...getNestedChildIds(itemId)] : [itemId]),
    [localeFolderIds, getNestedChildIds]
  );

  const onItemSelect = (itemId) => {
    setSelectedItemsMap((prevState) => {
      const shouldSelect = !prevState[itemId];
      const idsToUpdate = getTargetIds(itemId);

      const nextState = { ...prevState };
      idsToUpdate.forEach((id) => {
        nextState[id] = shouldSelect;
      });

      if (!shouldSelect) {
        getAncestorFolderIds(itemId).forEach((ancestorId) => {
          nextState[ancestorId] = false;
        });
      }

      return nextState;
    });
  };

  const isAllItemsSelected = useMemo(() => {
    const allItems = [...allLocalePages, ...foldersForLocale];

    return allItems.length > 0 && allItems.every((item) => selectedItemsMap[item.id]);
  }, [allLocalePages, foldersForLocale, selectedItemsMap]);

  const folderSelectionStateById = useMemo(
    () =>
      getFolderSelectionStateById(items, {
        selectedItemsMap,
        getNestedChildIds,
      }),
    [items, selectedItemsMap, getNestedChildIds]
  );

  const onAllItemsSelect = useCallback(() => {
    setSelectedItemsMap((prevState) => {
      const itemIds = items.flatMap((item) => getTargetIds(item.id));
      const toggle = (shouldSelect) =>
        toggleItemsSelection({
          selectedItemsMap: prevState,
          itemIds,
          shouldSelect,
          folderIds: localeFolderIds,
          childIdsByParentId,
        });

      const selected = toggle(true);

      return selected === prevState ? toggle(false) : selected;
    });
  }, [items, getTargetIds, localeFolderIds, childIdsByParentId]);

  const updateSharedState = useCallback(
    (customData) => {
      resolveSharedData({
        pageManagerTreeState: {
          ...pageManagerTreeState,
          ...customData,
        },
      });
    },
    [resolveSharedData, pageManagerTreeState]
  );

  const handleOpenNodeIdsChange = useCallback(
    (openNodeIds) => {
      updateSharedState({ openNodeIds });
    },
    [updateSharedState]
  );

  const handleScrollToItemHandled = useCallback(() => {
    updateSharedState({ scrollToItemId: null });
  }, [updateSharedState]);

  const onClearSelection = useCallback(() => {
    setSelectedItemsMap(buildSelectedItemsMap(allLocalePages, foldersForLocale));
  }, [allLocalePages, foldersForLocale]);

  const onCancelSelection = useCallback(
    (itemsCount) => {
      console.log(itemsCount);

      onClearSelection();
      trackEvent(events.PAGE_MANAGER_BULK_EDITING_CANCELLED, { itemsCount });
    },
    [onClearSelection]
  );

  const handleItemMove = useCallback(
    ({ itemIds, newParentId }) => {
      onItemMove({ itemIds, newParentId });

      // A multi-item move is a completed bulk action: clear the selection and
      // leave select mode, like the other bulk operations do when they finish.
      if (itemIds.length > 1) {
        onClearSelection();
      }
    },
    [onItemMove, onClearSelection]
  );

  const selectedPagesCount = useMemo(
    () => allLocalePages.filter((page) => selectedItemsMap[page.id]).length,
    [allLocalePages, selectedItemsMap]
  );

  const selectedFoldersCount = useMemo(
    () => foldersForLocale.filter((folder) => selectedItemsMap[folder.id]).length,
    [foldersForLocale, selectedItemsMap]
  );

  const selectedItemsCount = selectedPagesCount + selectedFoldersCount;

  const getSelectedItemIds = useCallback(() => {
    const selectedIds = Object.keys(selectedItemsMap).filter((id) => selectedItemsMap[id]);

    return {
      pageIds: selectedIds.filter((id) => !localeFolderIds.has(id)),
      folderIds: selectedIds.filter((id) => localeFolderIds.has(id)),
    };
  }, [selectedItemsMap, localeFolderIds]);

  const getAdditionalActionSettings = (actionId) => {
    if (selectedItemsCount === 0) {
      return {
        disabled: true,
      };
    }

    if (actionId === 'translatePage' && selectedPagesCount === 0) {
      return {
        disabled: true,
        tooltip: i18next.t('Select pages to continue'),
      };
    }

    if (actionId === 'moveToFolder') {
      const tooltip = getMoveToFolderTooltip({
        ...getSelectedItemIds(),
        folders: foldersForLocale,
        pages: allLocalePages,
      });

      if (tooltip) {
        return { disabled: true, tooltip };
      }
    }

    if (actionId === 'delete') {
      const homePage = allLocalePages.find((page) =>
        site.verifiers.isHomePage(page.path, helpers.getLanguageCodes())
      );
      const foldersById = new Map(foldersForLocale.map((folder) => [folder.id, folder]));
      const homePageFolderIds = new Set();
      let homeFolderId = homePage?.parentFolderId;
      while (homeFolderId) {
        homePageFolderIds.add(homeFolderId);
        homeFolderId = foldersById.get(homeFolderId)?.parentFolderId;
      }

      const isHomePageSelected = selectedItemsMap[homePage.id];
      const selectedFolders = foldersForLocale.filter((folder) => selectedItemsMap[folder.id]);
      const isFolderWithHomePageSelected = selectedFolders.some((folder) =>
        homePageFolderIds.has(folder.id)
      );

      if (isHomePageSelected || isFolderWithHomePageSelected) {
        return {
          disabled: true,
          tooltip: (
            <>
              {i18next.t("Home page can't be deleted")}
              <br />
              {i18next.t('Deselect it to delete the rest')}
            </>
          ),
        };
      }

      return {
        component: DeleteTitleComponent,
      };
    }

    return {};
  };

  const bulkPagesActions = [
    {
      id: 'translatePage',
      iconGlyph: 'translate',
      title: `${i18next.t('Translate to')}...`,
      ...getAdditionalActionSettings('translatePage'),
      onClick: () =>
        handleTranslatePages({ pageIds: getSelectedItemIds().pageIds, bulkEditing: true }),
    },
    {
      id: 'moveToFolder',
      iconGlyph: 'folder-move',
      title: `${i18next.t('Move to')}...`,
      ...getAdditionalActionSettings('moveToFolder'),
      onClick: () => handleMoveItems({ ...getSelectedItemIds(), bulkEditing: true }),
    },
    {
      id: 'delete',
      iconGlyph: 'trash',
      iconType: 'error',
      title: i18next.t('Delete'),
      divider: 'before',
      ...getAdditionalActionSettings('delete'),
      onClick: () => handleDeleteItems({ ...getSelectedItemIds(), bulkEditing: true }),
    },
  ];

  useEffect(() => {
    setSelectedItemsMap((prevState) =>
      buildSelectedItemsMap(allLocalePages, foldersForLocale, prevState)
    );
  }, [allLocalePages, foldersForLocale]);

  return (
    <PageManager
      items={items}
      isPageCreationAvailable={isPageCreationAvailable(pageTemplates)}
      onItemClick={onItemClick}
      onClose={onClose}
      onAddNewPage={handleAddNewPageClick}
      onCreateFolder={handleCreateFolderClick}
      isPageFoldersEnabled={Boolean(isPageFoldersEnabled)}
      onSearchClick={trackPageManagerSearchClicked}
      selectModeEnabled={selectModeEnabled}
      setSelectModeEnabled={setSelectModeEnabled}
      filter={filter}
      captions={captions}
      optionsToCreatePage={aiPageWizardEnabled ? optionsToCreatePage : null}
      onTemplateItemClick={handleOpenPageCreation}
      bulkPagesActions={bulkPagesActions}
      isAllItemsSelected={isAllItemsSelected}
      selectedItemsCount={selectedItemsCount}
      selectedItemsMap={selectedItemsMap}
      folderSelectionStateById={folderSelectionStateById}
      savedOpenFolderIds={savedOpenFolderIds}
      defaultOpenFolderIdsOnSearch={defaultOpenFolderIdsOnSearch}
      searchQuery={searchQuery}
      onOpenNodeIdsChange={handleOpenNodeIdsChange}
      onItemSelect={onItemSelect}
      onClearSelection={onClearSelection}
      onItemMove={handleItemMove}
      onAllItemsSelect={onAllItemsSelect}
      onCancelSelection={onCancelSelection}
      scrollToItemId={scrollToItemId}
      onScrollToItemHandled={handleScrollToItemHandled}
      upgradeBanner={{
        ...upgradeBannerVisibilityItems,
        title: `${i18next.t('Pages')}:`,
        buttonText: i18next.t('Get more'),
        current: pages.length,
        total: pagesFeatureLimit.amount,
        onUpgrade: () => {
          upsells.pagesCount.onUpgrade().catch(() => {
            console.error('Upsell cancelled');
          });
        },
      }}
    />
  );
}

PageManagerContainer.propTypes = {
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.string,
    })
  ).isRequired,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      locale: PropTypes.string,
    })
  ).isRequired,
  locale: PropTypes.string.isRequired,
  onDeletePageClick: PropTypes.func.isRequired,
  onPageSettingsClick: PropTypes.func.isRequired,
  onSetHomePageClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSetActivePage: PropTypes.func.isRequired,
  onAddNewPage: PropTypes.func.isRequired,
  onCopyPage: PropTypes.func.isRequired,
  onAddNewPageClick: PropTypes.func.isRequired,
  onDeveloperConsoleClick: PropTypes.func.isRequired,
  onGeneratePageCopy: PropTypes.func.isRequired,
  openPageCreation: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
  onFolderSettingsClick: PropTypes.func.isRequired,
  onDeleteFolderClick: PropTypes.func.isRequired,
  onItemMove: PropTypes.func.isRequired,
  activePageId: PropTypes.string.isRequired,
  currentPackageType: PropTypes.string.isRequired,
  pageTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  savingStatus: PropTypes.string.isRequired,
  onUnsavedActivePageChange: PropTypes.func.isRequired,
  html: PropTypes.string.isRequired,
  htmlCheckpoint: PropTypes.string.isRequired,
  featureFlags: PropTypes.shape({
    developer_console: PropTypes.bool,
    ai_page_wizard: PropTypes.bool,
    ai_localization: PropTypes.bool,
    ai_generate_page_copy: PropTypes.bool,
    page_folders: PropTypes.bool,
  }).isRequired,
  upsells: PropTypes.shape({
    pagesCount: PropTypes.shape({
      onUpgrade: PropTypes.func,
    }),
  }).isRequired,
  currentPackage: PropTypes.shape().isRequired,
  businessCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]).isRequired,
  // eslint-disable-next-line yola/react/no-unused-prop-types
  onDialogCancel: PropTypes.func.isRequired,
  isReadOnlyModeEnabled: PropTypes.bool.isRequired,
  siteSettings: PropTypes.shape({
    [settingTypes.BUSINESS_NAME]: PropTypes.string,
    [settingTypes.BUSINESS_DESCRIPTION]: PropTypes.string,
  }).isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const pageTemplates = template.selectors.getPageTemplates(state);
  const activePageId = site.selectors.getActivePageId(state);
  const activePage = site.selectors.getPage(state, activePageId);
  const htmlCheckpoint = activePage && activePage.htmlCheckpoint;
  const html = activePage && activePage.html;
  const locale = siteLocales.selectors.getCurrentLocale(state);
  const currentPackage = selectors.getCurrentPackage(state);
  const upsells = integration.selectors.getUpsells(state);
  const isReadOnlyModeEnabled = readOnly.selectors.getReadOnlyStatus(state);
  const siteSettings = site.selectors.getSettings(state);
  const currentPackageType = selectors.getCurrentPackageType(state);

  return {
    upsells,
    currentPackage,
    pageTemplates,
    activePageId,
    htmlCheckpoint,
    html,
    locale,
    pages: site.selectors.getPages(state),
    folders: site.selectors.getFolders(state),
    businessCategory: site.selectors.getBusinessCategory(state),
    savingStatus: saving.selectors.getStatus(state),
    isReadOnlyModeEnabled,
    siteSettings,
    currentPackageType,
  };
};

const mapDispatchToProps = (dispatch, { featureFlags, onDialogCancel }) => {
  const actions = {
    onDeletePageClick(pageId, pageTitle, selectedModeEnabled) {
      dispatch(
        dialogs.actions.show(dialogTypes.DELETE_PAGE, {
          pageId,
          pageTitle,
          itemType: itemTypes.PAGE,
          bulkEditing: selectedModeEnabled,
          itemsCount: 1,
        })
      );
    },

    onPageSettingsClick(pageId) {
      dispatch(dialogs.actions.show(dialogTypes.PAGE_SETTINGS, { pageId }));
    },

    onSetHomePageClick(pageId, locale) {
      dispatch(
        site.actions.setHomePage(
          pageId,
          locale,
          site.verifiers.isMultilingualSite(),
          helpers.getLanguageCodes()
        )
      );
      saveSite();
    },

    onClose() {
      onDialogCancel();
      dispatch(dialogs.actions.hide());
    },

    onSetActivePage(pageId) {
      dispatch(site.actions.setActivePage(pageId));
    },

    onUnsavedActivePageChange(callback) {
      dispatch(
        dialogs.actions.show(dialogTypes.UNSAVED_WARNING, {
          callback,
          closingCallback: () => dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER)),
        })
      );
    },

    onAddNewPageClick(options) {
      dispatch(dialogs.actions.show(dialogTypes.NEW_PAGE_DIALOG, { ...options }));
    },

    openPageCreation(options) {
      dispatch(dialogs.actions.show(dialogTypes.ADD_PAGE_DIALOG, { ...options }));
    },

    onCreateFolderClick(locale) {
      const onCreateFolder = (folderName) => {
        dispatch(site.actions.addFolder({ folderName, locale }));
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
      };

      dispatch(
        dialogs.actions.show(dialogTypes.CREATE_FOLDER_DIALOG, {
          onSubmit: onCreateFolder,
          triggerId: triggerIds.ADD_FOLDER_PLUS_BUTTON,
        })
      );
    },

    onFolderSettingsClick(folderId, folderName) {
      dispatch(dialogs.actions.show(dialogTypes.FOLDER_SETTINGS, { folderId, folderName }));
    },

    async onDeleteFolderClick(folderId, folderName, selectModeEnabled, isFolderEmpty = false) {
      if (isFolderEmpty) {
        const state = store.getState();
        const pages = site.selectors.getPages(state);
        const activePageId = site.selectors.getActivePageId(state);

        await performFolderDelete({
          folderId,
          pages,
          activePageId,
        });

        saveSite();
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
        return;
      }

      dispatch(
        dialogs.actions.show(dialogTypes.DELETE_FOLDER, {
          folderId,
          folderName,
          itemType: itemTypes.FOLDER,
          bulkEditing: selectModeEnabled,
          itemsCount: 1,
        })
      );
    },

    async onAddNewPage(values, parentFolderId) {
      const { business_info_in_website_settings: isBusinessInfoInWebsiteSettingsEnabled } =
        featureFlags;
      const {
        [fieldNames.PAGE_NAME_INPUT]: pageTitle,
        [fieldNames.PAGE_TEMPLATE_SELECT]: selectedPageTemplate,
        [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: displayInNavigation,
      } = values;
      const pageModifiers = [];

      if (selectedPageTemplate === constants.SHOP_PAGE_TEMPLATE_ID) {
        pageModifiers.push(injectStoreId);
      }

      if (selectedPageTemplate === pageTypes.ALL_BLOCKS) {
        pageModifiers.push(insertAllBlocksToThePage(featureFlags), injectStoreId);
      }

      const pageModifier = (documentPage) => {
        pageModifiers.forEach((fn) => fn(documentPage));
      };

      const pageTemplate =
        !selectedPageTemplate || selectedPageTemplate === pageTypes.ALL_BLOCKS
          ? constants.BLANK_PAGE_TEMPLATE_ID
          : selectedPageTemplate;

      const onAfterAddNewPage = () => {
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));

        if (selectedPageTemplate === pageTypes.ALL_BLOCKS) {
          setTimeout(() => {
            const currentState = store.getState();
            const pageId = site.selectors.getActivePageId(currentState);
            const { html: pageHtml } = site.selectors.getPage(currentState, pageId);
            const usedBlocks = blocks.helpers.getUsedBlocksFromHTML(pageHtml);

            dispatch(site.actions.setPageUsedBlocks(pageId, usedBlocks));
            // This is a workaround to reload all previously added blocks
            dispatch(view.actions.forceReloadView());
          }, 0);
        }
      };

      hooks.operations.subscribe(hooks.availableHooks.ON_SITE_LOADED, onAfterAddNewPage, {
        once: true,
      });

      const useBusinessNameInPageTitle = isBusinessInfoInWebsiteSettingsEnabled ? true : null;

      try {
        await site.operations.createPageFromTemplate({
          pageTemplateId: pageTemplate,
          title: pageTitle,
          displayInNavigation,
          pageModifier,
          useBusinessNameInPageTitle,
          ...(parentFolderId && { parentFolderId }),
        });
      } catch (e) {
        hooks.operations.unsubscribe(hooks.availableHooks.ON_SITE_LOADED, onAfterAddNewPage);
      }
    },

    onCopyPage(targetPageId, locale, values) {
      const {
        [fieldNames.PAGE_NAME_INPUT]: pageTitle,
        [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: displayInNavigation,
      } = values;

      dispatch(
        site.actions.copyPage(
          targetPageId,
          locale,
          pageTitle,
          displayInNavigation,
          site.verifiers.isMultilingualSite()
        )
      );

      hooks.operations.subscribe(hooks.availableHooks.ON_BEFORE_PAGE_LOADED, saveSite, {
        once: true,
      });

      dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
    },

    async onGeneratePageCopy(pageId, locale) {
      try {
        await initiatePageReplacement({
          pageId,
          locale,
          enforceTextRegeneration: true,
        });
        await reloadSiteMeta();

        dispatch(site.actions.setActivePage(pageId));

        const onSiteLoaded = () => {
          saveSiteWithEffect(() => {
            dispatch(dialogs.actions.hide());
          });
        };

        hooks.operations.subscribe(hooks.availableHooks.ON_SITE_LOADED, onSiteLoaded, {
          once: true,
        });
      } catch (e) {
        const errorType = e.results?.code || errorTypes.UNKNOWN_REPLACE_PAGE;

        dispatch(
          dialogs.actions.show(dialogTypes.AI_ERROR_DIALOG, {
            errorType,
            onRenew: () => {
              dispatch(dialogs.actions.hide());
            },
          })
        );
      }
    },

    onDeveloperConsoleClick(page) {
      dispatch(dialogs.actions.show(dialogTypes.DEVELOPER_CONSOLE, { page }));
    },

    onItemMove({ itemIds, newParentId }) {
      const targetParentFolderId = newParentId === ROOT_ID ? null : newParentId;
      itemIds.forEach((itemId) => {
        dispatch(site.actions.moveToFolder(itemId, targetParentFolderId));
      });
    },
  };

  return actions;
};

export default withFeatureFlags([
  'developer_console',
  'ai_page_wizard',
  'business_info_in_website_settings',
  'hidden_blocks',
  'contact_forms_disabled',
  'ai_localization',
  'ai_generate_page_copy',
  'page_folders',
])(connect(mapStateToProps, mapDispatchToProps)(PageManagerContainer));
